import React, { useEffect, useState, useCallback } from "react";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Spinner,
  Card,
  Button,
  InlineGrid,
  BlockStack,
  Text,
  Badge,
  InlineStack,
  TextField,
  Divider,
  DropZone,
} from "@shopify/polaris";
import {
  ThumbsUpIcon,
  ThumbsDownIcon,
  NoteIcon,
  DeleteIcon,
} from "@shopify/polaris-icons";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatFriendlyDate } from "../../utility/funcs";
import { copyValidacionDocsToClient } from "../../utility/clientDocuments";
import "./ViewValidacion.css";

function ViewValidacion() {
  const navigate = useNavigate();
  const { validacionId } = useParams();
  const { session } = UserAuth();
  const userId = session?.user?.id;

  const [validacion, setValidacion] = useState(null);
  const [files, setFiles] = useState([]);
  const [votes, setVotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [votingLoading, setVotingLoading] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [visitComment, setVisitComment] = useState("");
  const [visitPhotos, setVisitPhotos] = useState([]);
  const [visitLoading, setVisitLoading] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);

  const handleVisitPhotoDrop = useCallback((_drop, accepted) => {
    setVisitPhotos((prev) => [...prev, ...accepted]);
  }, []);

  const fetchData = async () => {
    setLoading(true);

    const [validRes, filesRes, votesRes, activityRes] = await Promise.all([
      supabase
        .from("validaciones")
        .select("*")
        .eq("id", validacionId)
        .single(),
      supabase
        .from("validacion_files")
        .select("*")
        .eq("validacion_id", validacionId)
        .order("created_at"),
      supabase
        .from("validacion_votes")
        .select("*, user:user_id(name)")
        .eq("validacion_id", validacionId),
      supabase
        .from("validacion_activity")
        .select("*, user:user_id(name)")
        .eq("validacion_id", validacionId)
        .order("created_at", { ascending: false }),
    ]);

    if (validRes.data) {
      setValidacion(validRes.data);
      if (validRes.data.car_category_id) {
        const { data: cat } = await supabase
          .from("car_categories")
          .select("name")
          .eq("id", validRes.data.car_category_id)
          .single();
        if (cat) setCategoryName(cat.name);
      }
    }
    if (filesRes.data) {
      // Generate signed URLs for all files so we can display previews
      const filesWithUrls = await Promise.all(
        filesRes.data.map(async (file) => {
          const { data } = await supabase.storage
            .from("validacion-docs")
            .createSignedUrl(file.file_url, 3600);
          return { ...file, signed_url: data?.signedUrl || null };
        })
      );
      setFiles(filesWithUrls);
    }
    if (votesRes.data) setVotes(votesRes.data);
    if (activityRes.data) setActivity(activityRes.data);

    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [validacionId]);

  const castVote = async (vote) => {
    setVotingLoading(true);

    // Upsert vote
    const { error } = await supabase.from("validacion_votes").upsert(
      {
        validacion_id: validacionId,
        user_id: userId,
        vote,
      },
      { onConflict: "validacion_id,user_id" }
    );

    if (error) {
      console.error(error.message);
      setVotingLoading(false);
      return;
    }

    // Log activity
    await supabase.from("validacion_activity").insert({
      validacion_id: validacionId,
      user_id: userId,
      action: vote === "approve" ? "vote_approve" : "vote_disapprove",
      content:
        vote === "approve" ? "Votó a favor" : "Votó en contra",
    });

    // Check if threshold reached (2 votes in either direction)
    const { data: allVotes } = await supabase
      .from("validacion_votes")
      .select("vote")
      .eq("validacion_id", validacionId);

    if (allVotes) {
      const approves = allVotes.filter((v) => v.vote === "approve").length;
      const disapproves = allVotes.filter((v) => v.vote === "disapprove").length;

      let newStatus = null;
      if (approves >= 2) newStatus = "approved";
      else if (disapproves >= 2) newStatus = "rejected";

      if (newStatus && validacion?.status === "pending") {
        const nowIso = new Date().toISOString();
        await supabase
          .from("validaciones")
          .update({ status: newStatus, updated_at: nowIso, decision_at: nowIso })
          .eq("id", validacionId);

        await supabase.from("validacion_activity").insert({
          validacion_id: validacionId,
          user_id: userId,
          action: newStatus === "approved" ? "vote_approve" : "vote_disapprove",
          content:
            newStatus === "approved"
              ? "Validación aprobada automáticamente (2 votos a favor)"
              : "Validación rechazada automáticamente (2 votos en contra)",
        });
      }
    }

    await fetchData();
    setVotingLoading(false);
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setCommentLoading(true);

    await supabase.from("validacion_activity").insert({
      validacion_id: validacionId,
      user_id: userId,
      action: "comment",
      content: comment.trim(),
    });

    setComment("");
    await fetchData();
    setCommentLoading(false);
  };

  const submitVisit = async () => {
    setVisitLoading(true);

    // Upload visit photos
    for (const file of visitPhotos) {
      const safeName = file.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `${validacionId}/visit_${Date.now()}_${safeName}`;
      const { error: uploadError } = await supabase.storage
        .from("validacion-docs")
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        console.error(uploadError);
        continue;
      }

      await supabase.from("validacion_files").insert({
        validacion_id: validacionId,
        file_type: "visit_photo",
        file_url: filePath,
        file_name: file.name,
      });
    }

    // Mark as visited
    await supabase
      .from("validaciones")
      .update({ visited: true, updated_at: new Date().toISOString() })
      .eq("id", validacionId);

    // Log visit activity
    await supabase.from("validacion_activity").insert({
      validacion_id: validacionId,
      user_id: userId,
      action: "visit",
      content: visitComment.trim() || "Registró una visita",
      metadata: { photo_count: visitPhotos.length },
    });

    setVisitComment("");
    setVisitPhotos([]);
    await fetchData();
    setVisitLoading(false);
  };

  // Create a customer profile from this validation row and copy its INE/license
  // documents into the customer's document slots.
  const createCustomerFromValidacion = async () => {
    setCreatingCustomer(true);
    try {
      // RFC and CURP are both one-per-person identifiers — dedup on either.
      const dupOrs = [];
      if (validacion.rfc) dupOrs.push(`rfc.eq.${validacion.rfc}`);
      if (validacion.curp) dupOrs.push(`curp.eq.${validacion.curp}`);
      if (dupOrs.length > 0) {
        const { data: dup } = await supabase
          .from("customers")
          .select("id")
          .or(dupOrs.join(","))
          .limit(1);
        if (dup && dup.length > 0) {
          if (
            window.confirm(
              "Ya existe un cliente con este RFC o CURP. ¿Abrir su perfil en lugar de crear uno nuevo?"
            )
          ) {
            navigate(`/customers/edit?id=${dup[0].id}`);
          }
          return;
        }
      }
      const lastName = [validacion.father_last_name, validacion.mother_last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
      const phoneDigits = (validacion.phones?.[0] || "").replace(/\D/g, "");
      const { data: created, error } = await supabase
        .from("customers")
        .insert({
          first_name: validacion.first_names || null,
          last_name: lastName || null,
          email: validacion.email || null,
          phone: phoneDigits || null,
          phone_country_code: "+52",
          rfc: validacion.rfc || null,
          curp: validacion.curp || null,
        })
        .select("id")
        .single();
      if (error) throw new Error(error.message);

      await copyValidacionDocsToClient({
        validacionId,
        entityType: "customer",
        entityId: created.id,
        createdBy: userId,
      });

      await supabase.from("validacion_activity").insert({
        validacion_id: validacionId,
        user_id: userId,
        action: "comment",
        content: `Se creó el cliente #${created.id} desde esta validación.`,
      });

      navigate(`/customers/edit?id=${created.id}`);
    } catch (e) {
      alert("Error al crear el cliente: " + (e.message || e));
    } finally {
      setCreatingCustomer(false);
    }
  };

  const getSignedUrl = async (filePath) => {
    const { data } = await supabase.storage
      .from("validacion-docs")
      .createSignedUrl(filePath, 3600);
    if (data?.signedUrl) window.open(data.signedUrl, "_blank");
  };

  const statusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge tone="success">Aprobada</Badge>;
      case "rejected":
        return <Badge tone="critical">Rechazada</Badge>;
      default:
        return <Badge tone="attention">Pendiente</Badge>;
    }
  };

  const fileTypeLabel = (type) => {
    const labels = {
      ine_front: "INE Frente",
      ine_back: "INE Reverso",
      license_front: "Licencia Frente",
      license_back: "Licencia Reverso",
      other: "Otro",
      visit_photo: "Foto de Visita",
    };
    return labels[type] || type;
  };

  const currentUserVote = votes.find((v) => v.user_id === userId);
  const approveCount = votes.filter((v) => v.vote === "approve").length;
  const disapproveCount = votes.filter((v) => v.vote === "disapprove").length;

  const activityIcon = (action) => {
    switch (action) {
      case "vote_approve":
        return <span style={{ color: "var(--p-color-text-success)" }}>+1</span>;
      case "vote_disapprove":
        return <span style={{ color: "var(--p-color-text-critical)" }}>-1</span>;
      case "file_added":
        return <span style={{ color: "var(--p-color-text-success)" }}>+</span>;
      case "file_removed":
        return <span style={{ color: "var(--p-color-text-critical)" }}>-</span>;
      case "visit":
        return <span style={{ color: "var(--p-color-text-info)" }}>&#x1f3e0;</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <AppWrapper>
        <Page>
          <Spinner size="small" />
        </Page>
      </AppWrapper>
    );
  }

  if (!validacion) {
    return (
      <AppWrapper>
        <Page title="Validación no encontrada" />
      </AppWrapper>
    );
  }

  return (
    <AppWrapper>
      <Page
        title={`${validacion.first_names} ${validacion.father_last_name} ${validacion.mother_last_name || ""}`}
        titleMetadata={
          <InlineStack gap="200">
            {statusBadge(validacion.status)}
            {validacion.visited && <Badge tone="info">Visitado</Badge>}
          </InlineStack>
        }
        backAction={{ content: "Validaciones", url: "/validaciones" }}
        primaryAction={
          <InlineStack gap="200">
            <Button
              onClick={() => navigate(`/validaciones/new?id=${validacionId}`)}
            >
              Editar
            </Button>
            <Button
              variant="primary"
              loading={creatingCustomer}
              onClick={createCustomerFromValidacion}
            >
              Crear Cliente
            </Button>
          </InlineStack>
        }
      >
        <InlineGrid columns={["twoThirds", "oneThird"]} gap="400">
          {/* Left Column */}
          <BlockStack gap="400">
            {/* Personal Info */}
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Información Personal</Text>
                <InlineGrid columns={3} gap="200">
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">
                      CURP
                    </Text>
                    <Text>{validacion.curp || "-"}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">
                      RFC Personal
                    </Text>
                    <Text>{validacion.rfc || "-"}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">
                      Correo Electrónico
                    </Text>
                    <Text>{validacion.email || "-"}</Text>
                  </BlockStack>
                </InlineGrid>
                {(validacion.company_name || validacion.company_rfc) && (
                  <InlineGrid columns={2} gap="200">
                    <BlockStack gap="100">
                      <Text variant="bodySm" tone="subdued">
                        Empresa
                      </Text>
                      <Text>{validacion.company_name || "-"}</Text>
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text variant="bodySm" tone="subdued">
                        RFC Empresa
                      </Text>
                      <Text>{validacion.company_rfc || "-"}</Text>
                    </BlockStack>
                  </InlineGrid>
                )}
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">
                    Teléfonos
                  </Text>
                  <InlineStack gap="200">
                    {validacion.phones && validacion.phones.length > 0 ? (
                      validacion.phones.map((phone, i) => (
                        <a key={i} href={`tel:${phone}`}>
                          <Text>{phone}</Text>
                        </a>
                      ))
                    ) : (
                      <Text>-</Text>
                    )}
                  </InlineStack>
                </BlockStack>
                {(categoryName || validacion.car_segment_text) && (
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">
                      Segmento de Vehículo
                    </Text>
                    <Text>
                      {[categoryName, validacion.car_segment_text]
                        .filter(Boolean)
                        .join(" — ")}
                    </Text>
                  </BlockStack>
                )}
                {(validacion.desired_from || validacion.desired_to) && (
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">
                      Fechas Deseadas
                    </Text>
                    <Text>
                      {validacion.desired_from || "?"} → {validacion.desired_to || "Sin definir"}
                    </Text>
                  </BlockStack>
                )}
                {validacion.story && (
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">
                      Historia / Motivo
                    </Text>
                    <Text>{validacion.story}</Text>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            {/* Documents */}
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Documentos</Text>
                {files.filter((f) => f.file_type !== "visit_photo").length === 0 ? (
                  <Text tone="subdued">Sin documentos</Text>
                ) : (
                  <div className="validacion-docs-grid">
                    {files.filter((f) => f.file_type !== "visit_photo").map((file) => (
                      <div
                        key={file.id}
                        className="validacion-doc-item"
                        onClick={() => file.signed_url && window.open(file.signed_url, "_blank")}
                      >
                        {file.signed_url ? (
                          <img
                            src={file.signed_url}
                            alt={fileTypeLabel(file.file_type)}
                            className="validacion-doc-preview"
                          />
                        ) : (
                          <div className="validacion-doc-thumbnail">
                            <NoteIcon />
                          </div>
                        )}
                        <BlockStack gap="050">
                          <Text variant="bodySm" fontWeight="semibold">
                            {fileTypeLabel(file.file_type)}
                          </Text>
                          <Text variant="bodySm" tone="subdued" truncate>
                            {file.file_name}
                          </Text>
                        </BlockStack>
                      </div>
                    ))}
                  </div>
                )}
              </BlockStack>
            </Card>

            {/* Visit */}
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd">Visita</Text>
                  {validacion.visited && <Badge tone="info">Visitado</Badge>}
                </InlineStack>

                {/* Show existing visit photos */}
                {files.filter((f) => f.file_type === "visit_photo").length > 0 && (
                  <div className="validacion-docs-grid">
                    {files
                      .filter((f) => f.file_type === "visit_photo")
                      .map((file) => (
                        <div
                          key={file.id}
                          className="validacion-doc-item"
                          onClick={() => file.signed_url && window.open(file.signed_url, "_blank")}
                        >
                          {file.signed_url ? (
                            <img
                              src={file.signed_url}
                              alt="Foto de Visita"
                              className="validacion-doc-preview"
                            />
                          ) : (
                            <div className="validacion-doc-thumbnail">
                              <NoteIcon />
                            </div>
                          )}
                          <BlockStack gap="050">
                            <Text variant="bodySm" fontWeight="semibold">
                              Foto de Visita
                            </Text>
                            <Text variant="bodySm" tone="subdued" truncate>
                              {file.file_name}
                            </Text>
                          </BlockStack>
                        </div>
                      ))}
                  </div>
                )}

                {/* Add visit form */}
                {!validacion.visited && (
                  <BlockStack gap="200">
                    <DropZone onDrop={handleVisitPhotoDrop} accept="image/*" variableHeight>
                      <DropZone.FileUpload actionTitle="Subir fotos de la visita" />
                    </DropZone>
                    {visitPhotos.length > 0 && (
                      <BlockStack gap="100">
                        {visitPhotos.map((f, i) => (
                          <InlineStack key={i} align="space-between" blockAlign="center">
                            <Text variant="bodySm">{f.name}</Text>
                            <Button
                              icon={DeleteIcon}
                              variant="plain"
                              tone="critical"
                              onClick={() =>
                                setVisitPhotos(visitPhotos.filter((_, idx) => idx !== i))
                              }
                            />
                          </InlineStack>
                        ))}
                      </BlockStack>
                    )}
                    <TextField
                      label="Comentario de visita"
                      labelHidden
                      value={visitComment}
                      onChange={setVisitComment}
                      multiline={2}
                      placeholder="Comentarios sobre la visita..."
                    />
                    <div>
                      <Button
                        variant="primary"
                        onClick={submitVisit}
                        loading={visitLoading}
                      >
                        Registrar Visita
                      </Button>
                    </div>
                  </BlockStack>
                )}

                {!validacion.visited &&
                  files.filter((f) => f.file_type === "visit_photo").length === 0 && (
                    <Text tone="subdued" variant="bodySm">
                      Aún no se ha registrado una visita
                    </Text>
                  )}
              </BlockStack>
            </Card>
          </BlockStack>

          {/* Right Column */}
          <BlockStack gap="400">
            {/* Voting */}
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Votación</Text>
                <InlineStack gap="200">
                  <Button
                    icon={ThumbsUpIcon}
                    variant={
                      currentUserVote?.vote === "approve"
                        ? "primary"
                        : undefined
                    }
                    tone="success"
                    onClick={() => castVote("approve")}
                    loading={votingLoading}
                    disabled={votingLoading}
                  >
                    Aprobar
                  </Button>
                  <Button
                    icon={ThumbsDownIcon}
                    variant={
                      currentUserVote?.vote === "disapprove"
                        ? "primary"
                        : undefined
                    }
                    tone="critical"
                    onClick={() => castVote("disapprove")}
                    loading={votingLoading}
                    disabled={votingLoading}
                  >
                    Rechazar
                  </Button>
                </InlineStack>
                <Text variant="bodySm" tone="subdued">
                  {approveCount} a favor, {disapproveCount} en contra
                </Text>
                <Divider />
                <BlockStack gap="200">
                  {votes.map((v) => (
                    <InlineStack key={v.id} gap="200" blockAlign="center">
                      {v.vote === "approve" ? (
                        <Badge tone="success">Si</Badge>
                      ) : (
                        <Badge tone="critical">No</Badge>
                      )}
                      <Text variant="bodySm">{v.user?.name || "Usuario"}</Text>
                    </InlineStack>
                  ))}
                </BlockStack>
              </BlockStack>
            </Card>

            {/* Activity Timeline */}
            <Card>
              <BlockStack gap="300">
                <Text variant="headingMd">Actividad</Text>

                {/* Add comment */}
                <BlockStack gap="200">
                  <TextField
                    label="Comentario"
                    labelHidden
                    value={comment}
                    onChange={setComment}
                    multiline={2}
                    placeholder="Escribe un comentario..."
                  />
                  <div>
                    <Button
                      onClick={addComment}
                      loading={commentLoading}
                      disabled={!comment.trim()}
                    >
                      Comentar
                    </Button>
                  </div>
                </BlockStack>

                <Divider />

                {/* Timeline */}
                <BlockStack gap="300">
                  {activity.map((item) => (
                    <div key={item.id} className="validacion-activity-item">
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center">
                          {activityIcon(item.action)}
                          <Text variant="bodySm" fontWeight="semibold">
                            {item.user?.name || "Usuario"}
                          </Text>
                          <Text variant="bodySm" tone="subdued">
                            {formatFriendlyDate(item.created_at)}
                          </Text>
                        </InlineStack>
                        {item.action === "comment" ? (
                          <div className="validacion-comment-bubble">
                            <Text variant="bodySm">{item.content}</Text>
                          </div>
                        ) : item.action === "field_updated" &&
                          item.metadata?.old_value !== undefined ? (
                          <BlockStack gap="050">
                            <Text variant="bodySm" tone="subdued">
                              {item.content}
                            </Text>
                            <div className="validacion-field-change">
                              <Text variant="bodySm">
                                <span className="field-old">
                                  {item.metadata.old_value || "(vacío)"}
                                </span>
                                {" → "}
                                <span className="field-new">
                                  {item.metadata.new_value || "(vacío)"}
                                </span>
                              </Text>
                            </div>
                          </BlockStack>
                        ) : (
                          <Text variant="bodySm" tone="subdued">
                            {item.content}
                          </Text>
                        )}
                        {/* Show thumbnail for file removed events */}
                        {item.action === "file_removed" &&
                          item.metadata?.file_url && (
                            <div
                              className="validacion-removed-file"
                              onClick={() =>
                                getSignedUrl(item.metadata.file_url)
                              }
                            >
                              <Text variant="bodySm" tone="subdued">
                                {item.metadata.file_name}
                              </Text>
                            </div>
                          )}
                      </BlockStack>
                    </div>
                  ))}
                  {activity.length === 0 && (
                    <Text tone="subdued" variant="bodySm">
                      Sin actividad
                    </Text>
                  )}
                </BlockStack>
              </BlockStack>
            </Card>
          </BlockStack>
        </InlineGrid>
      </Page>
    </AppWrapper>
  );
}

export default ViewValidacion;
